import { SKALA_ZNAKU, cn } from '@insolitum/ui';
import { Check } from 'lucide-react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';

import { LanguageSwitcher } from '../LanguageSwitcher';

import { ZnakWejscia } from './ZnakWejscia';

/**
 * Tło ekranów wejścia — ta sama poświata na rejestracji, resecie hasła, 2FA,
 * zaproszeniu i `/m` (aktywacja, PIN). Logowanie ma mocniejszą odmianę
 * w swoim układzie hero.
 */
export const TLO_WEJSCIA =
  'aurora-grain bg-aurora-bg bg-[radial-gradient(120%_90%_at_30%_10%,rgba(var(--ids-brand-rgb),0.18),transparent_55%),radial-gradient(90%_80%_at_90%_100%,rgba(139,92,246,0.14),transparent_50%)]';

/**
 * Tekst na poświacie w trybie jasnym — zmienne na korzeniu ekranu wejścia,
 * nie klasy przy każdym tekście, bo obejmują też ekrany, które o poświacie
 * nic nie wiedzą (rejestracja, reset, 2FA, zaproszenie — każdy przez
 * `UkladWejscia`).
 *
 * Dlaczego mocniejszy tekst, a nie słabsza poświata: `--ids-text-muted`
 * (#64748B) ma na płaskim `--ids-bg` 4,55:1, czyli 0,05 zapasu — każde
 * zabarwienie tła spycha go pod 4,5 (zmierzone na poświacie: 3,5–4,4:1),
 * więc „słabsza poświata pod tekstem" znaczy w praktyce „bez poświaty".
 * Poświata jest cechą rodziny (tak samo stoi za ekranem startowym), więc to
 * tekst dostaje stopień mocniejszy: `--cosmos-500` (#475569), 5,6:1 na
 * najciemniejszym miejscu poświaty. Tokeny Aurory zostają nietknięte poza
 * tymi ekranami.
 *
 *  - `--ids-text-subtle` (#94A3B8, 2,1–2,6:1) zrównuje się ze stonowanym: na
 *    poświacie nie ma jaśniejszego stopnia tekstu, który miałby ≥ 4,5:1,
 *    a przypisy i tak odróżnia stopień pisma (12 px wobec 14–15 px);
 *  - wysoki kontrast w jasnym ma własny, mocniejszy stonowany (#334155) —
 *    tego nie osłabiamy (`:not([data-contrast=high])`), a subtelny idzie za
 *    nim, bo jest odwołaniem do stonowanego;
 *  - gradient nagłówka (`.grad-text`) w jasnym zaczynał się od cyan-400:
 *    1,4:1 na poświacie przy 36 px — poniżej 3:1, progu WCAG dla dużego
 *    tekstu. Niebieski → indygo → fiolet (600) daje 4,0–4,9:1. W ciemnym
 *    gradient zostaje (≥ 3,6:1).
 *
 * Tryb ciemny bez zmian: stonowany 6,0:1, subtelny ≥ 4,5:1 na poświacie.
 */
export const TEKST_NA_POSWIACIE =
  '[[data-mode=light]:not([data-contrast=high])_&]:[--ids-text-muted:var(--cosmos-500)] ' +
  '[[data-mode=light]_&]:[--ids-text-subtle:var(--ids-text-muted)] ' +
  '[[data-mode=light]_&]:[--ids-grad-a:var(--blue-600)] ' +
  '[[data-mode=light]_&]:[--ids-grad-b:var(--indigo-600)] ' +
  '[[data-mode=light]_&]:[--ids-grad-c:var(--violet-600)]';

/**
 * Bursztyn programu partnerskiego jako TEKST (link „Dołącz do programu
 * partnerskiego", „Zaloguj się" i regulamin na rejestracji partnera, „Pobierz
 * dane"). Na ciemnym amber-400 (≥ 8:1); w jasnym amber-400 dawał na karcie
 * 1,67:1, a `--ids-warning` (amber-600) 3,0:1 — amber-800 ma ≥ 5,6:1 także
 * na poświacie.
 */
export const TEKST_PARTNERA = 'text-amber-400 [[data-mode=light]_&]:text-amber-800';
export const LINK_PARTNERA =
  'text-amber-400 hover:text-amber-300 [[data-mode=light]_&]:text-amber-800 [[data-mode=light]_&]:hover:text-amber-900';

/**
 * Ikona stanu w kółku z tintą (sukces, ostrzeżenie, błąd) — grafika, więc
 * ≥ 3:1 względem tła (WCAG 1.4.11). Stopień 400 na tincie 20 % dawał w jasnym
 * 1,44–2,13:1 (zmierzone 05.10.2026), 700 daje ≥ 4,4:1; w ciemnym 400 zostaje.
 * Jedno źródło dla wszystkich ekranów wejścia, bo kółko stanu jest wspólną
 * figurą rodziny (sukces rejestracji, resetu, zaproszenia, wygasły link).
 */
export const IKONA_SUKCESU = 'text-green-400 [[data-mode=light]_&]:text-green-700';
export const IKONA_OSTRZEZENIA = 'text-amber-400 [[data-mode=light]_&]:text-amber-700';
export const IKONA_BLEDU = 'text-red-400 [[data-mode=light]_&]:text-red-700';

/**
 * Ramka pola formularza ekranów wejścia — granica pola ≥ 3:1 względem tła
 * (WCAG 1.4.11). `--ids-border` dawał 1,16:1 w ciemnym i 1,23:1 w jasnym,
 * czyli pole czytało się jak zwykły tekst na karcie. Te same wartości co
 * pole Genesis (`GenesisOtwarcie`): w ciemnym `--ids-text-muted`, w jasnym
 * #64748B (cosmos-400) — dosłownie, bo `TEKST_NA_POSWIACIE` podnosi tu
 * stonowany do #475569, a ramka nie potrzebuje stopnia tekstu.
 *
 * Wyłącznie poza fokusem (`:not(:focus)`): ramka w fokusie zostaje z Aurory
 * (`--ids-brand`), a reguła jasnego trybu ma specyficzność 0,2,0 — tyle co
 * `.input:focus`, więc bez tego zastrzeżenia wygrywałaby z fokusem.
 * Token `--ids-border` zostaje nietknięty (reszta aplikacji: rejestr długu).
 */
export const RAMKA_POLA_WEJSCIA =
  '[&:not(:focus)]:border-aurora-muted [[data-mode=light]_&:not(:focus)]:border-cosmos-400';

/**
 * Fokus pola ekranów wejścia: do ramki w `--ids-brand` dochodzi pierścień
 * 1 px pełnego koloru marki i poświata 3 px. Bez pierścienia fokus był
 * zmianą ramki z szarej na niebieską (1,5:1 względem nowej ramki) — granica
 * ≥ 3:1 sprawiła, że spoczynek i fokus przestały się różnić. Pas tuż za
 * ramką zmienia się teraz z tła na kolor marki: ≥ 3:1 w obu trybach.
 */
export const FOKUS_POLA_WEJSCIA =
  'focus:shadow-[0_0_0_1px_rgb(var(--ids-brand-rgb)),0_0_0_4px_rgba(var(--ids-brand-rgb),0.18)]';

/** Pole tekstowe ekranu wejścia: ramka ≥ 3:1 i fokus odróżnialny od spoczynku. */
export const POLE_WEJSCIA = `${RAMKA_POLA_WEJSCIA} ${FOKUS_POLA_WEJSCIA}`;

/**
 * Pole wyboru ekranów wejścia. Natywne pole Chromium ma ramkę #767676
 * (4,5:1), ale rysuje ją motyw systemu w pozycji ułamkowej, bez
 * przyciągania do piksela — w rejestracji wychodziło 1,94:1 w jasnym
 * i 2,69:1 w ciemnym. Tu ramka jest zwykłą ramką CSS (przyciąganą do
 * piksela) w tych samych kolorach co pola tekstowe, a zaznaczenie to
 * wypełnienie akcentem z ikoną `Check`:
 *  - `marka` (rejestracja): `--ids-brand`, znacznik biały (≥ 3,6:1);
 *  - `partner` (rejestracja partnera): amber-400 ze znacznikiem w atramencie
 *    w ciemnym, amber-700 z białym w jasnym — amber-500 z `accent-color`
 *    dawał w jasnym 2,15:1 do karty.
 */
export function PoleWyboruWejscia({
  akcent = 'marka',
  className,
  ...pole
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { akcent?: 'marka' | 'partner' }) {
  return (
    <span className={cn('relative inline-grid h-4 w-4 shrink-0 place-items-center', className)}>
      <input
        type="checkbox"
        {...pole}
        className={cn(
          'peer col-start-1 row-start-1 m-0 h-4 w-4 cursor-pointer appearance-none rounded-[4px] border bg-aurora-surface outline-none',
          RAMKA_POLA_WEJSCIA,
          'focus-visible:shadow-[0_0_0_1px_rgb(var(--ids-brand-rgb)),0_0_0_4px_rgba(var(--ids-brand-rgb),0.18)]',
          akcent === 'marka'
            ? 'checked:!border-aurora-brand checked:bg-aurora-brand'
            : 'checked:!border-amber-400 checked:bg-amber-400 [[data-mode=light]_&]:checked:!border-amber-700 [[data-mode=light]_&]:checked:bg-amber-700'
        )}
      />
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className={cn(
          'pointer-events-none col-start-1 row-start-1 hidden h-3 w-3 peer-checked:block',
          akcent === 'marka'
            ? 'text-aurora-on-accent'
            : 'text-cosmos-950 [[data-mode=light]_&]:text-aurora-on-accent'
        )}
      />
    </span>
  );
}

/**
 * Przycisk języka w rogu: cel dotyku 44 px na każdym wskaźniku (na
 * zgrubnym i tak tyle wymusza Aurora) i jawny poziomy odstęp glifu od
 * krawędzi pudełka — 15 px wypełnienia + 1 px przezroczystej ramki
 * `.iconbtn`. Bez jawnego wypełnienia glif stał na środku pudełka 40 px,
 * więc o „wcięcie" zależne od szerokości kodu języka od krawędzi.
 */
const PRZYCISK_JEZYKA_PX = 44;
const WCIECIE_GLIFU_PX = 16;
/**
 * Górna krawędź wersalików 12 px nad środkiem wiersza:
 * (wysokość wersalików − (wznoszenie − zejście) / 2) × stopień =
 * (0,727 − (0,969 − 0,242) / 2) × 12 ≈ 4,4 px dla Intera (inne kroje
 * bezszeryfowe ±0,3 px). Przycisk centruje glif w pionie, więc góra glifu
 * leży 44 / 2 − 4,4 = 17,6 px pod górą pudełka.
 */
const WERSALIKI_NAD_SRODKIEM_PX = 4.4;

/**
 * Przełącznik języka ekranów wejścia — stałe miejsce w całej rodzinie,
 * liczone do GLIFU, nie do pudełka przycisku (margines optyczny).
 *
 * Desktop: glif kończy się 48 px od prawej krawędzi — tyle, ile logo
 * zaczyna się od lewej (do 05.10.2026 równe były pudełka, a glif stał
 * 61 px od krawędzi); w pionie na osi środka logo (pojemnik ma wysokość
 * `LogoProduktu` ze `SKALA_ZNAKU`, `items-center`).
 *
 * Telefon: górna krawędź glifu na linii górnej krawędzi znaku (24 px +
 * wcięcie systemowe; było 41), a glif kończy się 24 px od prawej — na
 * marginesie treści.
 */
export function PrzelacznikJezykaWejscia() {
  return (
    <div
      className="absolute right-[calc(1.5rem_-_var(--ids-wejscie-wciecie-glifu))] top-[calc(1.5rem_+_env(safe-area-inset-top)_-_var(--ids-wejscie-glif-od-gory))] z-20 lg:right-[calc(3rem_-_var(--ids-wejscie-wciecie-glifu))] lg:top-12 lg:flex lg:h-[var(--ids-wejscie-logo-h)] lg:items-center [&_.iconbtn]:h-[var(--ids-wejscie-przycisk)] [&_.iconbtn]:w-auto [&_.iconbtn]:max-w-none [&_.iconbtn]:px-[calc(var(--ids-wejscie-wciecie-glifu)_-_1px)]"
      style={
        {
          '--ids-wejscie-logo-h': `${SKALA_ZNAKU.naglowekWejscia}px`,
          '--ids-wejscie-przycisk': `${PRZYCISK_JEZYKA_PX}px`,
          '--ids-wejscie-wciecie-glifu': `${WCIECIE_GLIFU_PX}px`,
          '--ids-wejscie-glif-od-gory': `${PRZYCISK_JEZYKA_PX / 2 - WERSALIKI_NAD_SRODKIEM_PX}px`,
        } as CSSProperties
      }
    >
      <LanguageSwitcher variant="minimal" />
    </div>
  );
}

/**
 * Układ ekranów wejścia jednokolumnowych (rejestracja, „nie pamiętasz
 * hasła", nowe hasło, 2FA, zaproszenie — każdy stan, także sukces).
 *
 * Do 05.10.2026 każdy z tych ekranów miał własne `min-h-screen flex
 * items-center`, więc znak lądował na wysokości wynikającej z długości
 * formularza: na telefonie od 24 do 152 px od góry, zależnie od ekranu.
 * Tu znak ma stałe miejsce, a treść ustawia się względem niego:
 *
 *  - telefon: znak pionowy z hasłem w przepływie 24 px od góry (+ wcięcie
 *    systemowe w trybie pełnoekranowym), treść 24 px pod hasłem (`mt-6`) —
 *    tyle, ile nagłówek pod hasłem na logowaniu, więc przejście logowanie
 *    → rejestracja nie przesuwa pierwszego tekstu;
 *  - desktop: logo z hasłem w rogu (48, 48), formularz na środku ekranu;
 *    `py-32` odsuwa długi formularz (rejestracja) od logo, gdy nie mieści się
 *    w wysokości ekranu.
 *
 * `szeroka`: kolumna 672 px zamiast 440 px — dla formularza w dwóch
 * kolumnach (rejestracja partnera: pola po dwa w wierszu, 16 województw po
 * trzy). Szerokość treści to jedyna różnica. Znak, przełącznik języka i tło
 * zostają tam, gdzie na pozostałych ekranach rodziny.
 *
 * `stalaGoraRem`: treść w STAŁYM punkcie od góry także na desktopie, zamiast
 * na środku ekranu — dla ekranu, który zmienia stan w obrębie jednego
 * przepływu (zaproszenie: przetwarzanie → wynik). Wyśrodkowana karta szła za
 * swoją wysokością, więc jej górna krawędź skakała między stanami (zaproszenie
 * na 1440 × 900: do 56 px). Górna krawędź treści stoi na
 * `max(8rem, 50dvh − stalaGoraRem)`: liczba to połowa wysokości NAJWYŻSZEGO
 * stanu ekranu, więc ten stan stoi na środku, a krótsze rosną od tego samego
 * punktu tylko w dół. 8rem to dotychczasowe `py-32` — odstęp od logo w rogu.
 * Telefon bez zmian (treść i tak stoi od góry, pod znakiem); bez tej opcji
 * każdy inny ekran rodziny zostaje na środku.
 */
export function UkladWejscia({
  children,
  className,
  szeroka = false,
  stalaGoraRem,
}: {
  children: ReactNode;
  className?: string;
  szeroka?: boolean;
  stalaGoraRem?: number;
}) {
  return (
    <div
      className={cn(
        TLO_WEJSCIA,
        TEKST_NA_POSWIACIE,
        'relative flex min-h-[100dvh] flex-col items-center px-6 pb-[calc(3rem_+_env(safe-area-inset-bottom))] pt-[calc(1.5rem_+_env(safe-area-inset-top))]',
        stalaGoraRem === undefined
          ? 'lg:justify-center lg:py-32'
          : 'lg:pt-[max(8rem,calc(50dvh_-_var(--ids-wejscie-stala-gora)))]',
        className
      )}
      style={
        stalaGoraRem === undefined
          ? undefined
          : ({ '--ids-wejscie-stala-gora': `${stalaGoraRem}rem` } as CSSProperties)
      }
    >
      <ZnakWejscia />
      <PrzelacznikJezykaWejscia />
      <div
        className={cn('relative z-10 mt-6 w-full lg:mt-0', szeroka ? 'max-w-2xl' : 'max-w-[440px]')}
      >
        {children}
      </div>
    </div>
  );
}
